import { ChevronsLeftRight } from 'lucide-react'
import { animate, useInView, useReducedMotion, type AnimationPlaybackControls } from 'motion/react'
import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
} from 'react'
import { cn } from '@/lib/utils'

interface Side {
  src: string
  alt: string
  label: string
}

interface BeforeAfterProps {
  /** Fica em cima, recortada até o divisor: o que se vê à esquerda. */
  before: Side
  /** Fica embaixo, inteira: aparece à direita do divisor. */
  after: Side
  width: number
  height: number
  className?: string
}

const KEY_STEP: Record<string, number> = {
  ArrowLeft: -5,
  ArrowRight: 5,
  PageDown: -20,
  PageUp: 20,
}

function clamp(value: number) {
  return Math.min(100, Math.max(0, value))
}

/**
 * Duas fotos alinhadas, uma sobre a outra, e um divisor que se arrasta para o
 * lado. As duas precisam ter o mesmo enquadramento — se o carro mudar de lugar
 * entre uma e outra, ele "pula" quando o divisor passa.
 *
 * Ao entrar na tela, o divisor sai quase da borda direita e para no meio: é a
 * dica de que a imagem se arrasta, e já mostra metade do problema.
 */
export function BeforeAfter({ before, after, width, height, className }: BeforeAfterProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const reduce = useReducedMotion()
  const inView = useInView(containerRef, { once: true, amount: 0.5 })
  const [position, setPosition] = useState(() => (reduce ? 50 : 88))
  const intro = useRef<AnimationPlaybackControls | null>(null)
  const dragging = useRef(false)

  useEffect(() => {
    if (!inView || reduce) return
    intro.current = animate(88, 50, {
      duration: 1.4,
      delay: 0.3,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: setPosition,
    })
    return () => intro.current?.stop()
  }, [inView, reduce])

  /** Qualquer gesto da pessoa interrompe a animação de entrada. */
  function takeOver(next: number) {
    intro.current?.stop()
    setPosition(clamp(next))
  }

  function positionAt(clientX: number) {
    const rect = containerRef.current?.getBoundingClientRect()
    if (!rect || rect.width === 0) return position
    return ((clientX - rect.left) / rect.width) * 100
  }

  function handlePointerDown(event: PointerEvent<HTMLDivElement>) {
    dragging.current = true
    event.currentTarget.setPointerCapture(event.pointerId)
    // No toque, o divisor só anda quando o dedo anda: assim, rolar a página
    // começando em cima da foto não o tira do lugar.
    if (event.pointerType !== 'touch') takeOver(positionAt(event.clientX))
  }

  function handlePointerMove(event: PointerEvent<HTMLDivElement>) {
    if (dragging.current) takeOver(positionAt(event.clientX))
  }

  function handlePointerEnd() {
    dragging.current = false
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const step = KEY_STEP[event.key]
    if (step !== undefined) {
      event.preventDefault()
      takeOver(position + step)
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault()
      takeOver(event.key === 'Home' ? 0 : 100)
    }
  }

  const rounded = Math.round(position)

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative cursor-ew-resize touch-pan-y overflow-hidden rounded-2xl bg-muted select-none',
        className,
      )}
      style={{ aspectRatio: `${width} / ${height}` }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
    >
      <img
        src={after.src}
        alt={after.alt}
        width={width}
        height={height}
        draggable={false}
        className="absolute inset-0 size-full object-cover"
      />
      <SideLabel className="right-3 bg-brand text-white">{after.label}</SideLabel>

      <div
        className="absolute inset-0"
        style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
      >
        <img
          src={before.src}
          alt={before.alt}
          width={width}
          height={height}
          draggable={false}
          fetchPriority="high"
          className="absolute inset-0 size-full object-cover"
        />
        <SideLabel className="left-3 bg-white/90 text-foreground">{before.label}</SideLabel>
      </div>

      <div className="pointer-events-none absolute inset-y-0" style={{ left: `${position}%` }}>
        <div className="absolute inset-y-0 w-0.5 -translate-x-1/2 bg-white shadow-[0_0_8px_rgba(0,0,0,0.35)]" />
        <div
          role="slider"
          tabIndex={0}
          aria-label="Comparar o carro do anúncio com os problemas ocultos"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={rounded}
          aria-valuetext={`${rounded}% ${before.label.toLowerCase()}, ${100 - rounded}% ${after.label.toLowerCase()}`}
          onKeyDown={handleKeyDown}
          className="pointer-events-auto absolute top-1/2 left-0 grid size-11 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-brand text-white shadow-lg ring-4 ring-white focus-visible:outline-offset-4"
        >
          <ChevronsLeftRight className="size-5" aria-hidden />
        </div>
      </div>
    </div>
  )
}

function SideLabel({ className, children }: { className: string; children: string }) {
  return (
    <span
      className={cn(
        'eyebrow pointer-events-none absolute top-3 rounded-full px-3 py-1.5 text-[10px] shadow-sm',
        className,
      )}
    >
      {children}
    </span>
  )
}
